import { useLanguage, useNetwork } from '@/src/contexts';
import { useEffect, useState } from 'react';
import {
  NETWORK,
  NETWORK_MAP,
  languageOptions,
  networks,
} from '@/src/helpers';
import { Language } from '@/src/models';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Page,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/src/ui-components/ui';

export default function PreferencesPage() {
  const { setNetwork, network } = useNetwork();
  const { language, setLanguage } = useLanguage();
  const [colorTheme, setColorTheme] = useState<string>('zerocap');

  // Load color theme from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('color-theme');
    if (saved) {
      setColorTheme(saved);
      document.documentElement.setAttribute('data-theme', saved);
    } else {
      // Set default zerocap theme if no saved preference
      document.documentElement.setAttribute('data-theme', 'zerocap');
    }
  }, []);

  // Handle color theme change
  const handleColorThemeChange = (newTheme: string) => {
    setColorTheme(newTheme);
    localStorage.setItem('color-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const networkOptions = networks.map((net) => ({
    value: net,
    label: NETWORK_MAP[net].name,
  }));

  const colorThemeOptions = [
    { value: 'zerocap', label: 'Zerocap (Default)' },
    { value: 'red', label: 'Red' },
    { value: 'zinc', label: 'Zinc' },
    { value: 'slate', label: 'Slate' },
    { value: 'stone', label: 'Stone' },
    { value: 'gray', label: 'Gray' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'rose', label: 'Rose' },
    { value: 'orange', label: 'Orange' },
    { value: 'green', label: 'Green' },
    { value: 'blue', label: 'Blue' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'violet', label: 'Violet' },
  ];

  return (
    <Page>
      <Page.Body className="p-6">
        <div className="mx-auto max-w-2xl space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Preferences</h1>
            <p className="text-muted-foreground">
              Manage your application settings and preferences
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Network</CardTitle>
              <CardDescription>
                Select which blockchain network to connect to
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="network">Network</Label>
                <Select
                  value={network}
                  onValueChange={(value) => setNetwork(value as NETWORK)}
                >
                  <SelectTrigger id="network">
                    <SelectValue placeholder="Select a network" />
                  </SelectTrigger>
                  <SelectContent>
                    {networkOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Color Theme</CardTitle>
              <CardDescription>
                Choose a color scheme for the interface
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="color-theme">Theme</Label>
                <Select
                  value={colorTheme}
                  onValueChange={handleColorThemeChange}
                >
                  <SelectTrigger id="color-theme">
                    <SelectValue placeholder="Select a color theme" />
                  </SelectTrigger>
                  <SelectContent>
                    {colorThemeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Language</CardTitle>
              <CardDescription>
                Choose your preferred language for the interface
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={language}
                  onValueChange={(value) => setLanguage(value as Language)}
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder="Select a language" />
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label} ({option.value})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      </Page.Body>
    </Page>
  );
}
